import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { getProduct, getProducts } from "../services/productService";
import toast from "react-hot-toast";
import sizeCharts from '../data/sizeCharts.json';
import getOptimizedUrl from '../utils/imageUtils';
import { isAccessory, isBottomProductType } from '../utils/productUtils';
import {
  CUSTOM_LENGTH_RANGE,
  CUSTOM_WAIST_RANGE,
  DEFAULT_CUSTOM_LENGTH,
  DEFAULT_CUSTOM_WAIST,
  formatCustomSizeLabel,
} from "../utils/customSizeUtils";
import {
  updateCart as apiUpdateCart,
  getCart as apiGetCart,
} from "../services/cartService";
import * as wishlistService from "../services/wishlistService";
import HomeMobileNavbar from "../components/HomeMobileNavbar";
import Navbar from "../components/Navbar";

// default sizes if product doesn't include sizes
const DEFAULT_SIZES = ["XXS", "XS", "S", "M", "L", "XL", "XXL"];

const CUSTOM_LENGTH_CHART = {
  height: ["5'' - 5'2", "5'3 - 5'4", "5'5 - 5'6", "5'7 - 5'8", "5'9 - 5'10", "5'10 - 6''"],
  recommended: [39, 40, 40, 41, 42, 43],
};

const rupee = (n) => `₹${n}`;
const ADD_TO_CART_LOGIN_NOTICE_KEY = "postAuthAddToCartNotice";

const ProductPage = () => {


    const [windowWidth, setWindowWidth] = React.useState(typeof window !== 'undefined' ? window.innerWidth : 1200);

  React.useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  const { productSlug } = useParams();
  const productIdentifier = productSlug;

  const [rawProduct, setRawProduct] = useState(null);
  const [product, setProduct] = useState(null); // normalized UI product
  const [suggestions, setSuggestions] = useState([]);
  const [colorVariants, setColorVariants] = useState([]);
  const [wishlistIds, setWishlistIds] = useState([]);
  const [selectedSize, setSelectedSize] = useState(null);
  const [isCustomSize, setIsCustomSize] = useState(false); // New state for custom flag
  const [qty, setQty] = useState(1);
  const [showSizeGuide, setShowSizeGuide] = useState(false);
  const [showCustomModal, setShowCustomModal] = useState(false);
  const [customLength, setCustomLength] = useState(DEFAULT_CUSTOM_LENGTH);
  const [customWaist, setCustomWaist] = useState(DEFAULT_CUSTOM_WAIST);
  const [showStickyOptions, setShowStickyOptions] = useState(false);
  const [hoveredSuggestion, setHoveredSuggestion] = useState(null);
  const [hideMobileCta, setHideMobileCta] = useState(false);
  const checkTheseOutRef = useRef(null);
  const shouldHideMobileCta = windowWidth < 768 && hideMobileCta;

  // Keep mobile CTA pinned until the "Check these out" section starts entering the viewport.
  useEffect(() => {
    const updateCtaVisibility = () => {
      if (typeof window === "undefined") return;

      if (window.innerWidth >= 768) {
        setHideMobileCta(false);
        return;
      }

      const sectionEl = checkTheseOutRef.current;
      if (!sectionEl) {
        setHideMobileCta(false);
        return;
      }

      const rect = sectionEl.getBoundingClientRect();
      const ctaHideThreshold = 110;

      setHideMobileCta(rect.top <= window.innerHeight - ctaHideThreshold);
    };

    updateCtaVisibility();
    window.addEventListener("scroll", updateCtaVisibility, { passive: true });
    window.addEventListener("resize", updateCtaVisibility);

    return () => {
      window.removeEventListener("scroll", updateCtaVisibility);
      window.removeEventListener("resize", updateCtaVisibility);
    };
  }, []);

  // Scroll to top on mount or product change
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [productIdentifier]);

  // Load wishlist
  useEffect(() => {
    const loadWishlist = async () => {
      const user = localStorage.getItem("user");
      const token = localStorage.getItem("token");
      if (user && token) {
        try {
          const data = await wishlistService.getWishlist();
          setWishlistIds(data.map((item) => item.product._id));
        } catch (error) {
          console.error("Failed to load wishlist:", error);
        }
      } else {
        setWishlistIds([]);
      }
    };
    loadWishlist();

    const onLogin = () => loadWishlist();
    const onLoginSuccess = () => {
      loadWishlist();
      try {
        const shouldShowNotice = sessionStorage.getItem(ADD_TO_CART_LOGIN_NOTICE_KEY) === "1";
        if (shouldShowNotice) {
          sessionStorage.removeItem(ADD_TO_CART_LOGIN_NOTICE_KEY);
          toast.success("Logged in. Add to cart now.");
        }
      } catch (e) {}
    };
    window.addEventListener("auth:login", onLogin);
    window.addEventListener("auth:login-success", onLoginSuccess);

    return () => {
      window.removeEventListener("auth:login", onLogin);
      window.removeEventListener("auth:login-success", onLoginSuccess);
    };
  }, []);

  const addToCart = async () => {
    // Check if user is logged in
    const user = localStorage.getItem("user");
    const token = localStorage.getItem("token");

    if (!user || !token) {
      // Force Login
      try {
        sessionStorage.setItem(ADD_TO_CART_LOGIN_NOTICE_KEY, "1");
      } catch (e) {}
      window.dispatchEvent(
        new CustomEvent("signup:toggle", {
          detail: {
            open: true,
            returnToCurrent: true,
            returnPath: `${window.location.pathname}${window.location.search}${window.location.hash}`,
          },
        }),
      );
      toast("Please login to add items to cart");
      return;
    }

    try {
      // Fetch latest cart from server to ensure sync
      let cart = [];
      try {
        cart = await apiGetCart();
      } catch (e) {
        console.error("Failed to fetch server cart", e);
        cart = [];
      }

      const existingIndex = cart.findIndex(
        (i) =>
          String(i.productId || i.id || i.product?._id || i.product) ===
            String(rawProduct._id) && i.size === selectedSize,
      );

      const customSizeLabel = formatCustomSizeLabel(customLength, customWaist);

      if (existingIndex > -1) {
        // Normalize quantity property
        const currentQty =
          cart[existingIndex].quantity || cart[existingIndex].qty || 0;
        cart[existingIndex].quantity = currentQty + qty;
        cart[existingIndex].qty = currentQty + qty; // Ensure both are set just in case
        // For custom bottoms, persist selected custom measurements.
        if (isCustomSize) {
          cart[existingIndex].customSize = customSizeLabel;
        }
      } else {
        // Prepare new item
        const __customVal = isCustomSize ? customSizeLabel : undefined;

        cart.push({
          productId: rawProduct._id,
          product: rawProduct.product || rawProduct.title,
          image: rawProduct.images?.[0] || "",
          size: selectedSize,
          customSize: (isCustomSize && __customVal) ? __customVal : undefined,
          quantity: qty,
          qty: qty,
          price:
            rawProduct.discountedPrice ||
            rawProduct.mrp ||
            rawProduct.price ||
            0,
        });
      }

      // Sync to backend first
      if (user && token) {
        try {
          const payload = cart.map((item) => ({
            product: item.product?._id || item.productId || item.product,
            qty: item.quantity || item.qty,
            size: item.size,
            customSize: item.customSize,
            price: item.price,
          }));

          await apiUpdateCart(payload);

          // Notify other parts of the app that cart changed
          window.dispatchEvent(new Event("cart:updated"));
        } catch (e) {
          console.error("cart sync failed", e);
          const backendMsg = e?.response?.data?.message;
          if (!backendMsg) {
            toast.error("Could not add to cart");
          }
          return; // Exit early on error
        }
      }

      // show base sliding toast (product added)
      toast.custom((t) => (
        <div
          className={`toast-slide-right ${t.visible ? "" : ""}`}
          style={{
            background: "#111",
            color: "#fff",
            padding: "12px 16px",
            borderRadius: 8,
            boxShadow: "0 6px 20px rgba(0,0,0,0.12)",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div style={{ fontWeight: 600 }}>
              Added to cart: {rawProduct.product || rawProduct.title}
            </div>
            <div style={{ marginLeft: "auto" }}>
              <button
                onClick={() => toast.dismiss(t.id)}
                style={{
                  background: "transparent",
                  color: "#fff",
                  opacity: 0.85,
                }}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      ));

      // show second toast for custom-size details only
      if (isCustomSize) {
        toast.custom((t) => (
          <div
            className={`toast-slide-right ${t.visible ? "" : ""}`}
            style={{
              background: "#111",
              color: "#fff",
              padding: "10px 14px",
              borderRadius: 8,
              boxShadow: "0 6px 20px rgba(0,0,0,0.12)",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <div className="text-sm text-white">
                {`Custom selected: Length ${customLength}", Waist ${customWaist}"`}
              </div>
              <div style={{ marginLeft: "auto" }}>
                <button
                  onClick={() => toast.dismiss(t.id)}
                  style={{
                    background: "transparent",
                    border: "none",
                    color: "#fff",
                    opacity: 0.85,
                  }}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        ));
      }
    } catch (err) {
      console.error(err);
      // Error already handled above with specific message
      // Only show generic error if we didn't already show a specific one
      if (!err?.response?.data?.message?.includes('not in stock')) {
        toast.error("Could not add to cart");
      }
    }
  };

  const toggleWishlist = async () => {
    if (!product) return;

    const user = localStorage.getItem("user");
    const token = localStorage.getItem("token");

    if (!user || !token) {
      toast.error("Please login to add items to wishlist");
      try {
        window.dispatchEvent(
          new CustomEvent("signup:toggle", {
            detail: {
              open: true,
              returnToCurrent: true,
              returnPath: `${window.location.pathname}${window.location.search}${window.location.hash}`,
            },
          })
        );
      } catch (e) {}
      return;
    }

    const isCurrentlyInWishlist = wishlistIds.includes(product.id);

    try {
      if (isCurrentlyInWishlist) {
        // Determine server-side entry and remove the correct size if present
        const serverList = await wishlistService.getWishlist();
        const entry = serverList.find(it => String(it.product?._id) === String(product.id));
        const sizeToRemove = entry?.size;
        await wishlistService.removeFromWishlist(product.id, sizeToRemove);
        // Refresh ids from server
        const updated = await wishlistService.getWishlist();
        setWishlistIds(updated.map(i => i.product._id));
        toast.success("Removed from wishlist");
        window.dispatchEvent(new CustomEvent("wishlist:updated", { detail: { wishlist: updated } }));
      } else {
        // Add to wishlist as product-only (no size). Sizes are set from the wishlist sidebar.
        await wishlistService.addToWishlist(product.id);
        const updated = await wishlistService.getWishlist();
        setWishlistIds(updated.map(i => i.product._id));
        toast.success("Added to wishlist!");
        window.dispatchEvent(new CustomEvent("wishlist:updated", { detail: { wishlist: updated } }));
      }
    } catch (error) {
      console.error("Wishlist error:", error);
      const backendMsg = error?.response?.data?.message;
      if (!backendMsg) toast.error("Failed to update wishlist");
    }
  };

  const navigate = useNavigate();

  const toggleWishlistItem = async (id, e) => {
    if (e) e.stopPropagation();
    const user = localStorage.getItem("user");
    const token = localStorage.getItem("token");

    if (!user || !token) {
      toast.error("Please login to add items to wishlist");
      try {
        window.dispatchEvent(
          new CustomEvent("signup:toggle", {
            detail: {
              open: true,
              returnToCurrent: true,
              returnPath: `${window.location.pathname}${window.location.search}${window.location.hash}`,
            },
          })
        );
      } catch (e) {}
      return;
    }

    const isCurrentlyInWishlist = wishlistIds.includes(id);

    try {
      if (isCurrentlyInWishlist) {
        const serverList = await wishlistService.getWishlist();
        const entry = serverList.find(it => String(it.product?._id) === String(id));
        const sizeToRemove = entry?.size;
        await wishlistService.removeFromWishlist(id, sizeToRemove);
        const updated = await wishlistService.getWishlist();
        setWishlistIds(updated.map(i => i.product._id));
        toast.success("Removed from wishlist");
        window.dispatchEvent(new CustomEvent("wishlist:updated", { detail: { wishlist: updated } }));
      } else {
        await wishlistService.addToWishlist(id);
        const updated = await wishlistService.getWishlist();
        setWishlistIds(updated.map(i => i.product._id));
        toast.success("Added to wishlist!");
        window.dispatchEvent(new CustomEvent("wishlist:updated", { detail: { wishlist: updated } }));
      }
    } catch (error) {
      console.error("Wishlist error:", error);
      const backendMsg = error?.response?.data?.message;
      if (!backendMsg) toast.error("Failed to update wishlist");
    }
  };

  const percentOff = product
    ? Math.round(
        ((product.originalPrice - product.price) / product.originalPrice) * 100,
      )
    : 0;

  // fetch product and suggestions
  useEffect(() => {
    let mounted = true;
    setRawProduct(null);
    setProduct(null);
    setSuggestions([]);
    setColorVariants([]);

    // Reset custom size state when product changes
    setIsCustomSize(false);
    setCustomLength(DEFAULT_CUSTOM_LENGTH);
    setCustomWaist(DEFAULT_CUSTOM_WAIST);
    setQty(1);

    // fetch main product
    getProduct(productIdentifier)
      .then((data) => {
        if (!mounted) return;
        // support two possible shapes: { product, suggestions } or plain product
        const payload = data.product
          ? data
          : { product: data, suggestions: [] };
        setRawProduct(payload.product);
        const d = payload.product;
        const isProductAccessory = isAccessory(d.productType);
        const ui = {
          id: d._id,
          title: d.product || d.title || "Product",
          description: d.productDescription || d.description || "",
          price: d.discountedPrice ?? d.mrp ?? 0,
          originalPrice: d.mrp ?? d.price ?? 0,
          images: Array.isArray(d.images) && d.images.length ? d.images : [""],
          sizes:
            isProductAccessory
              ? ['Freesize']
              : (Array.isArray(d.sizes) && d.sizes.length > 0
                  ? d.sizes
                  : DEFAULT_SIZES),
        };
        setProduct(ui);
        setSelectedSize(isProductAccessory ? 'Freesize' : (ui.sizes[0]));

        // color variants
        if (Array.isArray(data.colorVariants)) {
          setColorVariants(data.colorVariants);
        }

        // suggestions from API response if present
        if (Array.isArray(payload.suggestions) && payload.suggestions.length) {
          const mapped = payload.suggestions.map((s) => ({
            id: s._id,
            slug: s.slug,
            title: s.product || s.title || "",
            images:
              Array.isArray(s.images) && s.images.length ? s.images : [""],
          }));
          setSuggestions(mapped);
        } else {
          // fallback: fetch other products
          getProducts()
            .then((list) => {
              if (!mounted || !Array.isArray(list)) return;
              const others = list
                .filter((p) => String(p._id) !== String(d._id))
                .slice(0, 6)
                .map((d) => ({
                  id: d._id,
                  slug: d.slug,
                  title: d.product || d.title || "",
                  images:
                    Array.isArray(d.images) && d.images.length
                      ? d.images
                      : [""],
                }));
              setSuggestions(others);
            })
            .catch((e) => console.error("suggestions error", e));
        }
      })
      .catch((err) => {
        console.error("Failed to load product", err);
        toast.error("Failed to load product");
      });

    return () => {
      mounted = false;
    };
  }, [productIdentifier]);

  // Helper: map size label to backend quantity fields
  const getStockForSize = (size) => {
    if (!rawProduct) return null;
    const s = String(size || '').toLowerCase();
    switch (s) {
      case 'xxs':
        return Number(rawProduct.sizeXXSQuantity || 0);
      case 'xs':
        return Number(rawProduct.sizeXSQuantity || 0);
      case 's':
        return Number(rawProduct.sizeSQuantity || 0);
      case 'm':
        return Number(rawProduct.sizeMQuantity || 0);
      case 'l':
        return Number(rawProduct.sizeLQuantity || 0);
      case 'xl':
        return Number(rawProduct.sizeXLQuantity || 0);
      case 'xxl':
        return Number(rawProduct.sizeXXLQuantity || 0);
      case 'freesize':
      case 'free size':
        return Number(rawProduct.freesizeQuantity || 0);
      default:
        // try direct lookup if backend uses dynamic keys
        return Number(rawProduct[`size${String(size).replace(/\s+/g, '')}Quantity`] || 0) || 0;
    }
  };

  // Update SEO meta tags when product changes
  useEffect(() => {
    if (!rawProduct) return;

    // Get SEO fields with fallbacks (description NOT used for search)
    const metaTitle = rawProduct.metaTitle || `${rawProduct.product || rawProduct.title} - Nufab`;
    const metaDescription = rawProduct.metaDescription || `Shop ${rawProduct.product || rawProduct.title} at Nufab`;
    const metaKeywords = rawProduct.seoKeywords || `${rawProduct.product}, ${rawProduct.category}, ${rawProduct.productType}, ${rawProduct.color}`;

    // Update document title
    document.title = metaTitle;

    // Update or create meta description
    let metaDescTag = document.querySelector('meta[name="description"]');
    if (!metaDescTag) {
      metaDescTag = document.createElement('meta');
      metaDescTag.name = 'description';
      document.head.appendChild(metaDescTag);
    }
    metaDescTag.content = metaDescription;

    // Update or create meta keywords
    let metaKeywordsTag = document.querySelector('meta[name="keywords"]');
    if (!metaKeywordsTag) {
      metaKeywordsTag = document.createElement('meta');
      metaKeywordsTag.name = 'keywords';
      document.head.appendChild(metaKeywordsTag);
    }
    metaKeywordsTag.content = metaKeywords;

    // Update or create Open Graph meta tags for social sharing
    let ogTitle = document.querySelector('meta[property="og:title"]');
    if (!ogTitle) {
      ogTitle = document.createElement('meta');
      ogTitle.setAttribute('property', 'og:title');
      document.head.appendChild(ogTitle);
    }
    ogTitle.content = metaTitle;

    let ogDescription = document.querySelector('meta[property="og:description"]');
    if (!ogDescription) {
      ogDescription = document.createElement('meta');
      ogDescription.setAttribute('property', 'og:description');
      document.head.appendChild(ogDescription);
    }
    ogDescription.content = metaDescription;

    // Add product image to Open Graph
    if (rawProduct.images && rawProduct.images[0]) {
      let ogImage = document.querySelector('meta[property="og:image"]');
      if (!ogImage) {
        ogImage = document.createElement('meta');
        ogImage.setAttribute('property', 'og:image');
        document.head.appendChild(ogImage);
      }
      ogImage.content = rawProduct.images[0];
    }

    // Cleanup function to reset to default
    return () => {
      document.title = 'Nufab';
    };
  }, [rawProduct]);

  if (!product) {
    return <div className="w-full py-32 text-center">Loading...</div>;
  }

  return (
    <div className="w-full mx-0 px-0 pb-24 md:pb-8 text-left font-bdogrotesk">
       {windowWidth < 1168 ? <HomeMobileNavbar /> : <Navbar />}
      {/* Color Variants Swatches */}

      {/* Size Guide Modal */}
      {showSizeGuide && (
        <>
          <div
            className="fixed inset-0 bg-black/60 z-[10000]"
            onClick={() => setShowSizeGuide(false)}
          />
          <div
            className="fixed inset-0 z-[10001] flex items-center justify-center p-2 sm:p-4"
            onClick={() => setShowSizeGuide(false)}
          >
            <div
              className="bg-white max-w-5xl w-full max-h-[95vh] sm:max-h-[90vh] overflow-y-auto font-bdogrotesk"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="px-3 py-3 sm:px-4 sm:py-4 md:px-8 md:py-6">
                <div className="flex items-center justify-between mb-4 sm:mb-6 md:mb-8">
                  <h2 className="text-sm sm:text-base md:text-lg font-medium font-bdogrotesk pr-2">
                    Size chart for{" "}
                    {rawProduct?.productType?.toLowerCase().includes("pant") ||
                    rawProduct?.productType?.toLowerCase().includes("short") ||
                    rawProduct?.productType?.toLowerCase().includes("skirt") ||
                    rawProduct?.productType?.toLowerCase().includes("bottom")
                      ? "bottoms"
                      : "tops & dresses"}{" "}
                    *
                  </h2>
                  <button
                    onClick={() => setShowSizeGuide(false)}
                    className="text-sm sm:text-base md:text-lg hover:text-gray-600 font-bdogrotesk font-medium flex-shrink-0"
                  >
                    Close
                  </button>
                </div>

                {/* Conditionally render table based on product type using JSON data */}
                {(() => {
                  const isBottom = rawProduct?.productType?.toLowerCase().includes('pant') || rawProduct?.productType?.toLowerCase().includes('short') || rawProduct?.productType?.toLowerCase().includes('skirt') || rawProduct?.productType?.toLowerCase().includes('bottom');
                  const chart = isBottom ? sizeCharts.bottoms : sizeCharts.tops;
                  const formatSizeValue = (value) =>
                    typeof value === "string" ? value.replace(/-/g, "\u2011") : value;

                  return (
                    <div>
                      <table className="w-full font-bdogrotesk text-xs sm:text-sm md:text-base lg:text-lg font-medium">
                        <thead>
                          <tr className="border-b-2 border-black">
                            {chart.headers.map((h, idx) => (
                              <th key={idx} className={`px-1 sm:px-2 md:px-4 py-2 sm:py-3 md:py-4 ${idx === 0 ? 'text-left' : 'text-left'} font-medium text-xs sm:text-sm md:text-base`}>
                                {h}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {chart.rows.map((row, rIdx) => (
                            <tr key={rIdx} className="border-b border-black">
                              <td className="px-1 sm:px-2 md:px-4 py-2 sm:py-3 md:py-4 text-left font-medium text-xs sm:text-sm md:text-base">{row.label}</td>
                              {row.values.map((val, cIdx) => (
                                <td key={cIdx} className="px-1 sm:px-2 md:px-4 py-2 sm:py-3 md:py-4 text-left font-medium text-xs sm:text-sm md:text-base">{formatSizeValue(val)}</td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      <p className="text-xs sm:text-sm md:text-base lg:text-lg mt-4 sm:mt-6 font-bdogrotesk font-medium">
                        * All measurements are in Inches * All measurements (except length) are of body NOT garments
                      </p>
                    </div>
                  );
                })()}
              </div>
            </div>
          </div>
        </>
      )}

      {/* Custom Size Modal */}
      {showCustomModal && isBottomProductType(rawProduct?.productType, rawProduct?.product || rawProduct?.title) && (
        <>
          <div
            className="fixed inset-0 bg-black/60 z-[10000]"
            onClick={() => setShowCustomModal(false)}
          />
          <div
            className="fixed inset-0 z-[10001] flex items-center justify-center p-4"
            onClick={() => setShowCustomModal(false)}
          >
            <div
              className="bg-white  w-full max-w-4xl max-h-[90vh] overflow-y-auto p-3 sm:p-5 md:p-8"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-end mb-4">
                <button
                  onClick={() => setShowCustomModal(false)}
                  className="text-base font-medium hover:text-gray-600"
                >
                  Close
                </button>
              </div>

              <div className="w-full overflow-x-auto">
                <table className="w-full min-w-[640px] border-collapse">
                  <tbody>
                    <tr className="border-b border-black/60">
                      <td className="py-3 pr-4 text-base font-medium">Height</td>
                      {CUSTOM_LENGTH_CHART.height.map((h) => (
                        <td key={h} className="py-3 px-2 sm:px-3 text-base font-medium text-left whitespace-nowrap">{h}</td>
                      ))}
                    </tr>
                    <tr className="border-b border-black/60">
                      <td className="py-3 pr-4 text-base font-medium leading-tight">Recommended<br/>Length</td>
                      {CUSTOM_LENGTH_CHART.recommended.map((len, idx) => (
                        <td key={len} className="py-3 px-2 sm:px-3 text-base font-medium text-left whitespace-nowrap">{idx === CUSTOM_LENGTH_CHART.recommended.length - 1 ? `${len}+` : len}"</td>
                      ))}
                    </tr>
                  </tbody>
                </table>
              </div>

              <p className="mt-4 text-xs text-gray-600 lg:hidden">Select length and waist</p>
              <div className="mt-2 lg:mt-6 flex flex-col lg:flex-row lg:items-center gap-3 lg:gap-4">
                <div className="flex items-center gap-3 overflow-x-auto lg:overflow-visible">
                  <div className="flex items-center gap-3 shrink-0">
                    <div className="text-xs lg:text-base font-medium whitespace-nowrap">
                      <span className="lg:hidden">Length</span>
                      <span className="hidden lg:inline">Custom Length</span>
                    </div>
                    <div className="inline-flex items-center border border-black h-9 sm:h-10 w-fit">
                      <button
                        type="button"
                        className="w-7 sm:w-9 h-full text-base sm:text-xl leading-none"
                        onClick={() => setCustomLength((prev) => Math.max(CUSTOM_LENGTH_RANGE.min, prev - 1))}
                      >
                        -
                      </button>
                      <div className="w-12 sm:w-16 text-center text-xs sm:text-sm font-medium">{customLength}"</div>
                      <button
                        type="button"
                        className="w-7 sm:w-9 h-full text-base sm:text-xl leading-none"
                        onClick={() => setCustomLength((prev) => Math.min(CUSTOM_LENGTH_RANGE.max, prev + 1))}
                      >
                        +
                      </button>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <div className="text-xs lg:text-base font-medium whitespace-nowrap">
                      <span className="lg:hidden">Waist</span>
                      <span className="hidden lg:inline">Custom Waist</span>
                    </div>
                    <div className="inline-flex items-center border border-black h-9 sm:h-10 w-fit">
                      <button
                        type="button"
                        className="w-7 sm:w-9 h-full text-base sm:text-xl leading-none"
                        onClick={() => setCustomWaist((prev) => Math.max(CUSTOM_WAIST_RANGE.min, prev - 1))}
                      >
                        -
                      </button>
                      <div className="w-12 sm:w-16 text-center text-xs sm:text-sm font-medium">{customWaist}"</div>
                      <button
                        type="button"
                        className="w-7 sm:w-9 h-full text-base sm:text-xl leading-none"
                        onClick={() => setCustomWaist((prev) => Math.min(CUSTOM_WAIST_RANGE.max, prev + 1))}
                      >
                        +
                      </button>
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => {
                    setIsCustomSize(true);
                    setSelectedSize("Custom");
                    setShowCustomModal(false);
                  }}
                  className="px-4 sm:px-6 h-12 bg-black text-white text-base font-medium w-full lg:w-auto lg:ml-auto"
                >
                  Order Custom Size
                </button>
              </div>

              <p className="mt-5 text-base font-medium leading-relaxed">
                Customisations are <span className="font-semibold">free of cost</span>! | Customised pieces are not applicable for return/exchange as they're made just for you!
              </p>
            </div>
          </div>
        </>
      )}

      {/* RESPONSIVE: Stack vertical on mobile (flex-col), horizontal on desktop (md:flex-row) */}
      <div className="flex flex-col md:flex-row w-full gap-3 px-2 md:px-2">
        {/* Left: Images */}
        <div className="w-full md:w-1/2 relative">
          {/* Heart button - mobile only, bottom-right of image */}
          <button
            aria-label={wishlistIds.includes(product.id) ? "Remove from wishlist" : "Add to wishlist"}
            onClick={toggleWishlist}
            className="md:hidden absolute bottom-6 right-6 z-10 p-3 rounded-full flex items-center justify-center"
            style={{ background: "transparent" }}
          >
            {wishlistIds.includes(product.id) ? (
              <svg width="24" height="24" viewBox="0 0 19 17" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M1.5 3H0.5V8H2V9.5H3.5V11H5V12.5H6.5V14H8V15.5H9V16.5H10V15.5H11V14H12.5V12.5H14V11H15.5V9.5H17V8H18.5V3H17V1.5H15.5V0.5H11.5V2H10V3H8.5V2H7V0.5H3V1.5H1.5V3Z" fill="#F31717" stroke="#F31717" />
              </svg>
            ) : (
              <svg width="24" height="24" viewBox="0 0 19 17" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M1.5 3H0.5V8H2V9.5H3.5V11H5V12.5H6.5V14H8V15.5H9V16.5H10V15.5H11V14H12.5V12.5H14V11H15.5V9.5H17V8H18.5V3H17V1.5H15.5V0.5H11.5V2H10V3H8.5V2H7V0.5H3V1.5H1.5V3Z" fill="white" stroke="black" />
              </svg>
            )}
          </button>
          {/* Thumbnails: Horizontal swipe carousel on mobile, vertical scroll on desktop */}
          {/* RESPONSIVE: Horizontal scroll with snap on mobile, vertical scroll on desktop */}
          <div className="mt-3 max-w-full h-auto md:h-[85vh] overflow-x-auto md:overflow-x-hidden md:overflow-y-auto snap-x snap-mandatory md:snap-none no-scrollbar show-scrollbar-md">
            <div className="flex flex-row md:flex-col gap-3 px-1">
              {product.images
                .filter((_, idx) => idx !== 1) // Modified: Filter out the second image (index 1)
                .map((img, idx) => (
                  <div
                    key={idx}
                    className="w-full flex-shrink-0 aspect-[3/4] overflow-hidden snap-center bg-white"
                  >
                    <img
                      src={img} // Use filtered image
                      alt={`${product.title} ${idx + 1}`}
                      className="w-full h-full object-cover bg-white"
                    />
                  </div>
                ))}
            </div>
          </div>
        </div>

        {/* Right: Details */}
        <div className="w-full md:w-1/2 pt-2 flex flex-col min-h-auto md:min-h-[85vh]">
          <div className="relative">
            {/* Heart button - desktop only */}
            <button
              aria-label={
                wishlistIds.includes(product.id)
                  ? "Remove from wishlist"
                  : "Add to wishlist"
              }
              onClick={toggleWishlist}
              className="hidden md:flex absolute right-0 top-0 p-2 rounded-full items-center justify-center"
              style={{ background: "transparent" }}
            >
              {/* Heart SVG - fills red when wishlisted */}
              {wishlistIds.includes(product.id) ? (
                <svg
                  width="26"
                  height="26"
                  viewBox="0 0 19 17"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M1.5 3H0.5V8H2V9.5H3.5V11H5V12.5H6.5V14H8V15.5H9V16.5H10V15.5H11V14H12.5V12.5H14V11H15.5V9.5H17V8H18.5V3H17V1.5H15.5V0.5H11.5V2H10V3H8.5V2H7V0.5H3V1.5H1.5V3Z"
                    fill="#F31717"
                    stroke="#F31717"
                  />
                </svg>
              ) : (
                <svg
                  width="26"
                  height="26"
                  viewBox="0 0 19 17"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M1.5 3H0.5V8H2V9.5H3.5V11H5V12.5H6.5V14H8V15.5H9V16.5H10V15.5H11V14H12.5V12.5H14V11H15.5V9.5H17V8H18.5V3H17V1.5H15.5V0.5H11.5V2H10V3H8.5V2H7V0.5H3V1.5H1.5V3Z"
                    fill="white"
                    stroke="black"
                  />
                </svg>
              )}
            </button>
            <p className="hidden md:block text-base text-black font-medium text-left w-full md:w-5/6">
              {product.description}
            </p>
            {/* RESPONSIVE: text-4xl on mobile, text-8xl on desktop */}
            <h1 className="text-4xl md:text-6xl lg:text-8xl text-black font-semibold leading-tight mt-4 text-left">
              {product.title}
            </h1>
          </div>

          <div className="hidden md:block mt-6">
            {isAccessory(rawProduct?.productType) ? (
              <div className="flex items-start gap-3 text-sm text-gray-500 text-left">
                <div className="px-3 py-2 text-left bg-white font-semibold text-black">
                  <span className="inline-block pb-3 border-b-2 border-black">
                    Freesize
                  </span>
                </div>
              </div>
            ) : (
              <div className="flex items-start gap-1 sm:gap-2 md:gap-3 text-xs sm:text-sm text-gray-500 text-left">
                {product.sizes.map((s) => (
                  <button
                    key={s}
                    onClick={() => {
                      setSelectedSize(s);
                      setIsCustomSize(false);
                    }}
                    className={`px-1.5 py-1.5 sm:px-2 sm:py-2 md:px-3 md:py-2 text-left border bg-white ${
                      selectedSize === s && !isCustomSize
                        ? 'border-black font-semibold text-black'
                        : 'border-gray-300 text-gray-600'
                    }`}
                  >
                    <span
                      className={
                        selectedSize === s && !isCustomSize
                          ? 'inline-block pb-2 sm:pb-3 border-b-2 border-black'
                          : ''
                      }
                    >
                      {s}
                    </span>
                  </button>
                ))}
                {isBottomProductType(rawProduct?.productType, rawProduct?.product || rawProduct?.title) && (
                  <button
                    onClick={() => {
                      setShowCustomModal(true);
                    }}
                    className={`px-1.5 py-1.5 sm:px-2 sm:py-2 md:px-3 md:py-2 text-left border bg-white ${
                      isCustomSize
                        ? "border-black font-semibold text-black"
                        : "border-gray-300 text-gray-600"
                    }`}
                  >
                    <span
                      className={
                        isCustomSize
                          ? "inline-block pb-2 sm:pb-3 border-b-2 border-black"
                          : ""
                      }
                    >
                      Custom
                    </span>
                  </button>
                )}
                <button
                  onClick={() => setShowSizeGuide(true)}
                  className={` py-1.5  sm:py-2 md:py-2 text-left border bg-white ${
                    showSizeGuide
                      ? "border-black font-semibold text-black"
                      : "border-gray-300 text-black"
                  }`}
                >
                  <span
                    className={
                      showSizeGuide
                        ? "inline-block pb-2 sm:pb-3 border-b-2 border-black"
                        : "inline-block pb-2 sm:pb-3 border-b-2 border-transparent"
                    }
                  >
                    Size Chart
                  </span>
                </button>
              </div>
            )}
          </div>

          <div className="hidden md:flex mt-6 items-center gap-6 text-left">
            <div className="flex items-center border border-black w-max">
              <button
                className="px-2 py-2 text-left"
                onClick={() => setQty((q) => Math.max(1, q - 1))}
              >
                -
              </button>
              <div className="px-2 text-left">{qty}</div>
              <button
                className="px-2 text-left"
                onClick={() => setQty((q) => q + 1)}
              >
                +
              </button>
            </div>
          </div>
          {/* Color variants - Desktop only (hidden on mobile) */}
          {colorVariants.length > 0 && (
            <div className="hidden md:flex gap-2 mt-8 overflow-x-auto pb-2">
              {colorVariants.map((variant) => (
                <button
                  key={variant._id}
                  onClick={() =>
                    navigate(`/product/${variant.slug || variant._id}`)
                  }
                  className="border-2 border-solid border-black p-0.5 bg-transparent flex flex-col items-center justify-center flex-shrink-0"
                  style={{
                    border:
                      String(variant._id) === String(rawProduct?._id)
                        ? "2px solid black"
                        : "1px solid black",
                  }}
                >
                  <img
                    src={getOptimizedUrl(variant.images?.[0] || '', 400)}
                    alt={variant.color || ""}
                    className="w-24 md:w-32 h-32 md:h-40 object-cover bg-white"
                  />
                </button>
              ))}
            </div>
          )}
          <div className="mt-6">
            <div className="flex items-baseline gap-2 text-left">
              <div className="text-gray-400 text-lg md:text-xl line-through">
                {rupee(product.originalPrice)}
              </div>
              <div className="text-xl font-semibold">
                {rupee(product.price)}
              </div>
              <div className="text-xl text-red-500">({percentOff}% Off)</div>
            </div>
          </div>

          <div
            className={`mt-auto md:bg-white md:pt-4 md:pb-2 md:flex md:flex-col md:gap-3 md:text-left md:z-10 md:static fixed bottom-0 left-0 right-0 z-[9999] bg-white py-3 md:px-0 md:py-0 transition-all duration-300 ease-out md:transition-none ${!showStickyOptions ? 'px-3' : 'px-0'} ${
              shouldHideMobileCta
                ? "opacity-0 translate-y-6 pointer-events-none"
                : "opacity-100 translate-y-0 pointer-events-auto"
            }`}
          >
            {!showStickyOptions && (
              <>
                <button
                  type="button"
                  onClick={() => setShowStickyOptions(true)}
                  style={{
                    border: "1px solid #000",
                    boxSizing: "border-box",
                    backgroundColor: "#fff",
                  }}
                  className="md:hidden group mt-0 w-full text-black bg-white px-3 py-2 flex items-center justify-between text-[15px] font-semibold tracking-wide"
                >
                  <div className="relative h-5 overflow-hidden">
                    <div className="relative flex flex-col transition-transform duration-300 ease-in-out group-hover:-translate-y-1/2">
                      <span className="flex h-5 items-center">Add To Cart</span>
                      <span className="flex h-5 items-center">Add To Cart</span>
                    </div>
                  </div>
                </button>
                <p className="md:hidden mt-1 text-center text-xs text-gray-500">
                  Proceed to size selection
                </p>
              </>
            )}

            {showStickyOptions && (
              <>
                <div className="md:hidden mb-2 overflow-x-auto no-scrollbar">
                  {isAccessory(rawProduct?.productType) ? (
                    <div className="flex items-center justify-between text-xs whitespace-nowrap w-full">
                      <button
                        type="button"
                        onClick={() => {
                          setSelectedSize('Freesize');
                          setIsCustomSize(false);
                        }}
                        className="text-black font-medium"
                      >
                        <span className="inline-block pb-1 border-b-2 border-black">Freesize</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => setShowSizeGuide(true)}
                        className={`${showSizeGuide ? 'text-black font-medium' : 'text-gray-400'}`}
                      >
                        <span
                          className={
                            showSizeGuide
                              ? 'inline-block pb-1 border-b-2 border-black'
                              : 'inline-block pb-1 border-b-2 border-transparent'
                          }
                        >
                          Size Chart
                        </span>
                      </button>
                    </div>
                  ) : (
                    <div className="flex items-center justify-between text-xs px-8 whitespace-nowrap w-full">
                      {product.sizes.map((s) => (
                        <button
                          key={`sticky-size-${s}`}
                          type="button"
                          onClick={() => {
                            setSelectedSize(s);
                            setIsCustomSize(false);
                          }}
                          className={`${selectedSize === s && !isCustomSize ? 'text-black font-medium' : 'text-gray-400'}`}
                        >
                          <span
                            className={
                              selectedSize === s && !isCustomSize
                                ? 'inline-block pb-1 border-b-2 border-black'
                                : 'inline-block pb-1 border-b-2 border-transparent'
                            }
                          >
                            {s}
                          </span>
                        </button>
                      ))}

                      {isBottomProductType(rawProduct?.productType, rawProduct?.product || rawProduct?.title) && (
                        <button
                          type="button"
                          onClick={() => setShowCustomModal(true)}
                          className={`${isCustomSize ? 'text-black font-medium' : 'text-gray-400'}`}
                        >
                          <span
                            className={
                              isCustomSize
                                ? 'inline-block pb-1 border-b-2 border-black'
                                : 'inline-block pb-1 border-b-2 border-transparent'
                            }
                          >
                            Custom
                          </span>
                        </button>
                      )}

                      <button
                        type="button"
                        onClick={() => setShowSizeGuide(true)}
                        className={`${showSizeGuide ? 'text-black font-medium' : 'text-gray-400'}`}
                      >
                        <span
                          className={
                            showSizeGuide
                              ? 'inline-block pb-1 border-b-2 border-black'
                              : 'inline-block pb-1 border-b-2 border-transparent'
                          }
                        >
                          Size Chart
                        </span>
                      </button>
                    </div>
                  )}
                </div>

                <div className="md:hidden w-full grid grid-cols-[minmax(0,1fr)_88px] gap-2 items-stretch">
                  {(() => {
                    const selectedStock = getStockForSize(selectedSize);
                    const selectedOutOfStock = selectedSize && selectedStock !== null && selectedStock <= 0;
                    return (
                      <button
                        type="button"
                        onClick={() => { if (!selectedOutOfStock) addToCart(); }}
                        disabled={selectedOutOfStock}
                        aria-disabled={selectedOutOfStock}
                        className={`${selectedOutOfStock ? 'mt-0 w-full h-[44px] bg-gray-200 text-gray-700 px-4 flex items-center justify-start text-[15px] font-semibold tracking-wide box-border cursor-not-allowed' : 'mt-0 w-full h-[44px] bg-black text-white px-4 flex items-center justify-start text-[15px] font-semibold tracking-wide box-border'}`}
                      >
                        <span className="font-bdogrotesk">{selectedOutOfStock ? 'Out of stock' : 'Add To Cart'}</span>
                      </button>
                    );
                  })()}

                  <div className="grid grid-cols-3 place-items-center border border-black bg-white h-[44px] w-full">
                    <button
                      type="button"
                      className="w-full h-full text-center"
                      onClick={() => setQty((q) => Math.max(1, q - 1))}
                    >
                      -
                    </button>
                    <div className="w-full text-center">{qty}</div>
                    <button
                      type="button"
                      className="w-full h-full text-center"
                      onClick={() => setQty((q) => q + 1)}
                    >
                      +
                    </button>
                  </div>
                </div>
              </>
            )}

            {(() => {
              const selectedStock = getStockForSize(selectedSize);
              const selectedOutOfStock = selectedSize && selectedStock !== null && selectedStock <= 0;
              return (
                <button
                  type="button"
                  onClick={() => { if (!selectedOutOfStock) addToCart(); }}
                  disabled={selectedOutOfStock}
                  aria-disabled={selectedOutOfStock}
                  className={`${selectedOutOfStock ? 'hidden md:flex group mt-0 w-full bg-gray-200 text-gray-700 px-3 py-3 items-center justify-between text-[15px] font-semibold tracking-wide box-border cursor-not-allowed' : 'hidden md:flex group mt-0 w-full bg-black text-white px-3 py-3 items-center justify-between text-[15px] font-semibold tracking-wide box-border'}`}
                >
                  <div className="relative h-5 overflow-hidden">
                    <div className="relative flex flex-col transition-transform duration-300 ease-in-out group-hover:-translate-y-1/2">
                      <span className="flex font-bdogrotesk h-5 items-center">
                        {selectedOutOfStock ? 'Out of stock' : 'Add To Cart'}
                      </span>
                      <span className="flex h-5 items-center">{selectedOutOfStock ? 'Out of Stock' : 'Add To Cart'}</span>
                    </div>
                  </div>
                </button>
              );
            })()}

            {/* <button
              type="button"
              onClick={toggleWishlist}
              style={{
                border: "1px solid #000",
                boxSizing: "border-box",
                backgroundColor: "#fff",
              }}
              className="group mt-0 w-full text-black bg-white px-3 py-3 flex items-center justify-between text-[15px] font-semibold tracking-wide "
            >
              <div className="relative h-5 overflow-hidden">
                <div className="relative flex flex-col transition-transform duration-300 ease-in-out group-hover:-translate-y-1/2">
                  <span className="flex h-5 items-center">
                    {wishlistIds.includes(product.id)
                      ? "Remove from Wishlist"
                      : "Add To Wishlist"}
                  </span>
                  <span className="flex h-5 items-center">
                    {wishlistIds.includes(product.id)
                      ? "Remove from Wishlist"
                      : "Add To Wishlist"}
                  </span>
                </div>
              </div>
            </button> */}
          </div>
          
          {/* Color variants - Mobile only (shown below buttons) */}
          {colorVariants.length > 0 && (
            <div className="flex md:hidden gap-2 mt-4 overflow-x-auto pb-2">
              {colorVariants.map((variant) => (
                <button
                  key={variant._id}
                  onClick={() =>
                    navigate(`/product/${variant.slug || variant._id}`)
                  }
                  className="border p-0.5 bg-transparent flex flex-col items-center justify-center flex-shrink-0"
                  style={{
                    border:
                      String(variant._id) === String(rawProduct?._id)
                        ? "2px solid black"
                        : "1px solid #ccc",
                  }}
                >
                  <img
                    src={getOptimizedUrl(variant.images?.[0] || '', 400)}
                    alt={variant.color || ""}
                    className="w-24 md:w-32 h-32 md:h-40 object-cover bg-white"
                  />
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="mt-8">
        <h3 className="hidden md:block text-base py-5 px-2 md:px-2 font-medium">Details</h3>
        <div className="md:hidden px-2 py-5 flex items-start gap-4">
          <h3 className="text-base font-medium flex-shrink-0 w-14">Details</h3>
          <div className="flex-1 min-w-0 pl-4">
            <p className="text-sm text-black leading-tight pt-1">
              {product.description || "Product details not available."}
            </p>
          </div>
        </div>
        <div className="max-w-screen px-2 md:px-2 mx-auto space-y-6">
          {/* Mobile layout */}
          <section className="md:hidden flex items-start gap-4 py-4">
            <div className="flex-shrink-0 w-10 text-sm text-black">001</div>
            <div className="flex-1 min-w-0 pl-8">
              <h2 className="text-4xl sm:text-4xl text-black font-medium leading-tight">Features</h2>
              <div className="mt-3 text-sm text-black leading-tight">
                {rawProduct?.features || "Feature details not available."}
              </div>
            </div>
          </section>

          {/* Desktop layout (unchanged) */}
          <section className="hidden md:flex md:flex-row md:items-start gap-4 md:gap-16 lg:gap-64 py-4">
            <div className="flex-shrink-0 w-12 md:w-20 text-sm text-black">
              001
            </div>
            <h2 className="flex-1 text-4xl md:text-6xl text-black font-semibold leading-tight md:-mr-2">
              Features
            </h2>
            <div className="flex-shrink-0 w-full md:w-1/3 text-sm text-black">
              {rawProduct?.features || "Feature details not available."}
            </div>
          </section>
          <div className="border-t border-black" />

          {/* Mobile layout */}
          <section className="md:hidden flex items-start gap-4 py-4">
            <div className="flex-shrink-0 w-10 text-sm text-black">002</div>
            <div className="flex-1 min-w-0 pl-8">
              <h2 className="text-4xl sm:text-4xl text-black font-medium leading-tight">Size & Fit</h2>
              <div className="mt-3 text-sm text-black leading-tight">
                {rawProduct?.sizeAndFit || "Size and fit details not available."}
              </div>
            </div>
          </section>

          {/* Desktop layout (unchanged) */}
          <section className="hidden md:flex md:flex-row md:items-start gap-4 md:gap-16 lg:gap-64 py-4">
            <div className="flex-shrink-0 w-12 md:w-20 text-sm text-black">
              002
            </div>
            <h2 className="flex-1 text-4xl md:text-6xl text-black font-semibold leading-tight md:-mr-2">
              Size & Fit
            </h2>
            <div className="flex-shrink-0 w-full md:w-1/3 text-sm text-black">
              {rawProduct?.sizeAndFit ||
                "Size and fit details not available."}
            </div>
          </section>
          <div className="border-t border-black" />

          {/* Mobile layout */}
          <section className="md:hidden flex items-start gap-4 py-4">
            <div className="flex-shrink-0 w-10 text-sm text-black">003</div>
            <div className="flex-1 min-w-0 pl-8">
              <h2 className="text-4xl sm:text-4xl text-black font-medium leading-tight">Material</h2>
              <div className="mt-3 text-sm text-black leading-tight">
                {rawProduct?.material || "Material details not available."}
              </div>
            </div>
          </section>

          {/* Desktop layout (unchanged) */}
          <section className="hidden md:flex md:flex-row md:items-start gap-4 md:gap-16 lg:gap-64 py-4">
            <div className="flex-shrink-0 w-12 md:w-20 text-sm text-black">
              003
            </div>
            <h2 className="flex-1 text-4xl md:text-6xl text-black font-semibold leading-tight md:-mr-2">
              Material
            </h2>
            <div className="flex-shrink-0 w-full md:w-1/3 text-sm text-black">
              {rawProduct?.material || "Material details not available."}
            </div>
          </section>
          <div className="border-t border-black" />
        </div>
      </div>
      <div ref={checkTheseOutRef} className="mt-8 px-2 md:px-2 font-bdogrotesk">
        <h3 className="text-base py-2 px-0 md:px-2 font-medium">
          Check these out
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 px-0 md:px-2">
          {suggestions.map((p) => {
            const isProductInWishlist = wishlistIds.includes(p.id);
            return (
              <div key={p.id} className="group text-left space-y-4">
                <div
                  className="w-full  aspect-[3/4] overflow-hidden relative cursor-pointer bg-white"
                  onMouseEnter={() => setHoveredSuggestion(p.id)}
                  onMouseLeave={() => setHoveredSuggestion(null)}
                  onClick={() => navigate(`/product/${p.slug || p.id}`)}
                >
                  <img
                    src={getOptimizedUrl(p.images[0] || '', 800)}
                    alt={p.title}
                    className={`w-full h-full object-cover bg-white transition-opacity duration-300 ${
                      hoveredSuggestion === p.id && p.images && p.images[1] ? 'opacity-0' : 'opacity-100'
                    }`}
                  />
                  {p.images && p.images[1] && (
                    <img
                      src={getOptimizedUrl(p.images[1] || '', 800)}
                      alt={p.title}
                      className={`w-full h-full object-cover bg-white transition-opacity duration-300 absolute inset-0 ${
                        hoveredSuggestion === p.id ? 'opacity-100' : 'opacity-0'
                      }`}
                    />
                  )}
                  <button
                    type="button"
                    aria-label={isProductInWishlist ? "Remove from wishlist" : "Add to wishlist"}
                    onClick={(e) => toggleWishlistItem(p.id, e)}
                    className="absolute bottom-1 right-1 p-2"
                  >
                    {isProductInWishlist ? (
                      <svg
                        width="26"
                        height="26"
                        viewBox="0 0 19 17"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M1.5 3H0.5V8H2V9.5H3.5V11H5V12.5H6.5V14H8V15.5H9V16.5H10V15.5H11V14H12.5V12.5H14V11H15.5V9.5H17V8H18.5V3H17V1.5H15.5V0.5H11.5V2H10V3H8.5V2H7V0.5H3V1.5H1.5V3Z"
                          fill="#F31717"
                          stroke="#F31717"
                        />
                      </svg>
                    ) : (
                      <svg
                        width="26"
                        height="26"
                        viewBox="0 0 19 17"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M1.5 3H0.5V8H2V9.5H3.5V11H5V12.5H6.5V14H8V15.5H9V16.5H10V15.5H11V14H12.5V12.5H14V11H15.5V9.5H17V8H18.5V3H17V1.5H15.5V0.5H11.5V2H10V3H8.5V2H7V0.5H3V1.5H1.5V3Z"
                          fill="white"
                          stroke="black"
                        />
                      </svg>
                    )}
                  </button>
                </div>
                <div className="mt-2 text-sm">{p.title}</div>
                <button
                  type="button"
                  onClick={() => navigate(`/product/${p.slug || p.id}`)}
                  className="group mt-0 w-full bg-black text-white px-3 py-2 flex items-center justify-between text-[15px] font-semibold tracking-wide box-border"
                >
                  <div className="relative h-5 overflow-hidden">
                    <div className="relative text-base flex flex-col transition-transform duration-300 ease-in-out group-hover:-translate-y-1/2">
                      <span className="flex font-medium font-bdogrotesk h-5 items-center">
                        Shop Now
                      </span>
                      <span className="flex font-bdogrotesk font-medium h-5 items-center">
                        Shop Now
                      </span>
                    </div>
                  </div>
                </button>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default ProductPage;